import { format } from 'date-fns';
import ru from 'date-fns/locale/ru';
import type { fabric } from 'fabric';
import { PlaceholderKey } from 'modules/manager/domain/placeholder/placeholder-keys';
import {
  DEFAULT_PLAYER_NAME_FORMAT,
  PLAYER_NAME_FORMAT_OPTIONS,
  PLAYER_NAME_FORMAT_SAMPLE,
  formatPlayerNameParts,
} from 'modules/manager/domain/placeholder/player-name-format';
import { DEFAULT_DATE_FORMAT, DEFAULT_SCORE_FORMAT } from 'modules/manager/domain/placeholder/resolver';
import React, { useCallback, useEffect, useRef } from 'react';
import { editorAccent } from '../../editor-accent';
import { registerInspector } from './inspector-registry';
import PlaceholderTransformSection from './placeholder-transform-section';
import ColorField from './shared/color-field';
import CommitNumberField from './shared/commit-number-field';
import CommitTextField from './shared/commit-text-field';
import { FONT_FAMILY_OPTIONS } from './shared/font-family-options';
import HelpBubble from './shared/help-bubble';
import InspectorRow from './shared/inspector-row';
import InspectorSection from './shared/inspector-section';
import MixedSelectField from './shared/mixed-select-field';
import PlaceholderIdentitySection from './shared/placeholder-identity-section';
import { useCommonFabricData } from './shared/use-common-fabric-data';
import type { ObjectInspectorProps } from './types';

const MIXED = '(разное)';

const FONT_WEIGHTS = [
  { value: 'normal', label: 'Обычный' },
  { value: 'bold', label: 'Жирный' },
  { value: '300', label: 'Лёгкий (300)' },
  { value: '500', label: 'Средний (500)' },
  { value: '700', label: 'Жирный (700)' },
];

const TEXT_ALIGNS = [
  { value: 'left', label: 'Влево' },
  { value: 'center', label: 'По центру' },
  { value: 'right', label: 'Вправо' },
];

const PlaceholderTextInspector: React.FC<ObjectInspectorProps> = ({ objects, editor }) => {
  const previewMode = useCommonFabricData<boolean>(objects, 'previewMode', editor);
  const sampleText = useCommonFabricData<string>(objects, 'sampleText', editor);
  const fontFamily = useCommonFabricData<string>(objects, 'fontFamily', editor);
  const fontWeight = useCommonFabricData<string>(objects, 'fontWeight', editor);
  const textAlign = useCommonFabricData<string>(objects, 'textAlign', editor);
  const fill = useCommonFabricData<string>(objects, 'fill', editor);
  const minFontSize = useCommonFabricData<number>(objects, 'minFontSize', editor);
  const maxFontSize = useCommonFabricData<number>(objects, 'maxFontSize', editor);
  const dateFormat = useCommonFabricData<string>(objects, 'dateFormat', editor);
  const scoreFormat = useCommonFabricData<string>(objects, 'scoreFormat', editor);
  const nameFormat = useCommonFabricData<string>(objects, 'nameFormat', editor);
  const placeholderKey = useCommonFabricData<string>(objects, 'placeholderKey', editor);

  // Format fields only make sense when the whole selection shares the key.
  const showDateFormat = !placeholderKey.isMixed && placeholderKey.value === PlaceholderKey.MATCH_DATE;
  const showScoreFormat = !placeholderKey.isMixed && placeholderKey.value === PlaceholderKey.SCORE;
  const showNameFormat = !placeholderKey.isMixed && placeholderKey.value === PlaceholderKey.PLAYER_NAME;

  const dateFormatExample = (() => {
    try {
      return format(new Date(), dateFormat.value || DEFAULT_DATE_FORMAT, { locale: ru });
    } catch {
      return 'Invalid format';
    }
  })();

  const refitAll = useCallback(() => {
    for (const o of objects) editor.placeholders?.refit(o as fabric.Group);
  }, [editor, objects]);

  const previewRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (previewRef.current) previewRef.current.indeterminate = previewMode.isMixed;
  }, [previewMode.isMixed, previewMode.value]);

  const handlePreviewToggle = () => {
    const next = previewMode.isMixed ? true : !(previewMode.value ?? false);
    previewMode.setValue(next || undefined);
    refitAll();
  };

  const sampleDisabled = !previewMode.value && !previewMode.isMixed;

  return (
    <>
      <InspectorSection title="Предпросмотр">
        <InspectorRow label="Режим предпросмотра">
          <CheckboxLabel>
            <input
              ref={previewRef}
              type="checkbox"
              checked={Boolean(previewMode.value) && !previewMode.isMixed}
              onChange={handlePreviewToggle}
            />
            <span>{previewMode.isMixed ? '(разное)' : 'показать только образец текста'}</span>
          </CheckboxLabel>
        </InspectorRow>
      </InspectorSection>
      <PlaceholderIdentitySection objects={objects} editor={editor} />
      {(showDateFormat || showScoreFormat || showNameFormat) && (
        <InspectorSection title="Формат">
          {showDateFormat && (
            <InspectorRow label="Дата">
              <FieldWithHelp
                help={
                  <span>
                    Формат даты с использованием{' '}
                    <a
                      href="https://date-fns.org/docs/format"
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: editorAccent.ink, textDecoration: 'underline' }}
                    >
                      токенов date-fns
                    </a>
                    . Пример: <code>{'dd MMMM, yyyy'}</code>. Оставьте пустым для формата по умолчанию: <code>{DEFAULT_DATE_FORMAT}</code>.
                  </span>
                }
              >
                <CommitTextField
                  value={dateFormat.isMixed ? '' : dateFormat.value ?? ''}
                  onCommit={(v) => {
                    dateFormat.setValue(v ? v : undefined);
                    refitAll();
                  }}
                  placeholder={dateFormat.isMixed ? MIXED : DEFAULT_DATE_FORMAT}
                />
              </FieldWithHelp>
            </InspectorRow>
          )}
          {showDateFormat && (
            <InspectorRow label="Пример">
              <CommitTextField value={dateFormatExample} onCommit={() => {}} disabled />
            </InspectorRow>
          )}
          {showScoreFormat && (
            <InspectorRow label="Счёт">
              <FieldWithHelp
                help={
                  <span>
                    Шаблон итогового счёта. <code>t1</code> = счёт хозяев, <code>t2</code> = счёт гостей. Примеры:{' '}
                    <code>{'t1 - t2'}</code>, <code>{'t1:t2'}</code>, <code>{'t1'}</code>. Оставьте пустым для формата по умолчанию:{' '}
                    <code>{DEFAULT_SCORE_FORMAT}</code>.
                  </span>
                }
              >
                <CommitTextField
                  value={scoreFormat.isMixed ? '' : scoreFormat.value ?? ''}
                  onCommit={(v) => {
                    scoreFormat.setValue(v ? v : undefined);
                    refitAll();
                  }}
                  placeholder={scoreFormat.isMixed ? MIXED : DEFAULT_SCORE_FORMAT}
                />
              </FieldWithHelp>
            </InspectorRow>
          )}
          {showNameFormat && (
            <InspectorRow label="ФИО">
              <FieldWithHelp
                help={
                  <span>
                    Как разложить имя игрока. Варианты показаны на примере{' '}
                    <code>{formatPlayerNameParts(PLAYER_NAME_FORMAT_SAMPLE)}</code>. Если у игрока нет
                    отчества, соответствующая часть просто не выводится — лишних точек и пробелов не
                    остаётся.
                  </span>
                }
              >
                <MixedSelectField
                  value={nameFormat.value}
                  isMixed={nameFormat.isMixed}
                  onChange={(v) => {
                    nameFormat.setValue(v);
                    refitAll();
                  }}
                  options={[...PLAYER_NAME_FORMAT_OPTIONS]}
                  fallback={DEFAULT_PLAYER_NAME_FORMAT}
                />
              </FieldWithHelp>
            </InspectorRow>
          )}
        </InspectorSection>
      )}
      <InspectorSection title="Образец текста">
        <InspectorRow label="Текст">
          <CommitTextField
            value={sampleText.isMixed ? '' : sampleText.value}
            onCommit={(v) => {
              sampleText.setValue(v);
              refitAll();
            }}
            multiline
            placeholder={sampleText.isMixed ? MIXED : 'Текст для предпросмотра'}
            disabled={sampleDisabled}
          />
        </InspectorRow>
      </InspectorSection>
      <InspectorSection title="Типографика">
        <InspectorRow label="Шрифт">
          <MixedSelectField
            value={fontFamily.value}
            isMixed={fontFamily.isMixed}
            onChange={(v) => {
              fontFamily.setValue(v);
              refitAll();
            }}
            options={FONT_FAMILY_OPTIONS}
            fallback="Arial"
          />
        </InspectorRow>
        <InspectorRow label="Начертание">
          <MixedSelectField
            value={fontWeight.value === undefined ? undefined : String(fontWeight.value)}
            isMixed={fontWeight.isMixed}
            onChange={(v) => {
              fontWeight.setValue(v);
              refitAll();
            }}
            options={FONT_WEIGHTS}
            fallback="normal"
          />
        </InspectorRow>
        <InspectorRow label="Выравнивание">
          <MixedSelectField
            value={textAlign.value}
            isMixed={textAlign.isMixed}
            onChange={(v) => {
              textAlign.setValue(v);
              refitAll();
            }}
            options={TEXT_ALIGNS}
            fallback="center"
          />
        </InspectorRow>
        <InspectorRow label="Цвет">
          <ColorField
            value={fill.isMixed ? undefined : fill.value ?? '#111827'}
            onChange={(v) => {
              fill.setValue(v);
              refitAll();
            }}
            placeholder={fill.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
      </InspectorSection>
      <InspectorSection title="Вписывание">
        <InspectorRow label="Мин. размер">
          <CommitNumberField
            value={minFontSize.isMixed ? undefined : minFontSize.value ?? 8}
            onCommit={(v) => {
              minFontSize.setValue(v);
              refitAll();
            }}
            min={1}
            placeholder={minFontSize.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
        <InspectorRow label="Макс. размер">
          <CommitNumberField
            value={maxFontSize.isMixed ? undefined : maxFontSize.value ?? 256}
            onCommit={(v) => {
              maxFontSize.setValue(v);
              refitAll();
            }}
            min={1}
            placeholder={maxFontSize.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
      </InspectorSection>
      <PlaceholderTransformSection objects={objects} editor={editor} />
    </>
  );
};

export default PlaceholderTextInspector;

registerInspector('placeholder-text', PlaceholderTextInspector);

const FieldWithHelp: React.FC<{ help: React.ReactNode; children: React.ReactNode }> = ({ help, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', minWidth: 0 }}>
    <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
    <HelpBubble content={help} />
  </div>
);

const CheckboxLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label
    style={{
      display: 'inline-flex',
      gap: 6,
      alignItems: 'center',
      fontSize: 12,
      color: '#374151',
      cursor: 'pointer',
    }}
  >
    {children}
  </label>
);
